import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import logoSrc from '../../assets/logos/ce_logo.png';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(221, 218, 211, 0.82);
  display: flex;
  align-items: center;
  z-index: 100;
  transition: all var(--transition-speed) ease;
  
  &.scrolled {
    box-shadow: 0 12px 32px rgba(33, 26, 18, 0.06);
    border-bottom-color: var(--color-border);
  }
`;

const NavContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 clamp(20px, 4vw, 48px);
  max-width: 1280px;
  margin: 0 auto;
  position: relative;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0;
  color: var(--color-primary);
  text-decoration: none;
  z-index: 110;
  transition: all var(--transition-speed) ease;
  
  &:hover {
    color: var(--color-primary);
  }
  
  &:focus {
    outline: 2px solid var(--color-accent);
    outline-offset: 4px;
    border-radius: var(--border-radius-sm);
  }
`;

const LogoImage = styled.img`
  height: 72px;
  width: auto;
  object-fit: contain;
  flex: 0 0 auto;
`;

const LogoText = styled.span`
  display: flex;
  flex-direction: column;
  line-height: 1;

  @media (max-width: 960px) {
    display: none;
  }
`;

const LogoPrimary = styled.span`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
`;

const LogoSecondary = styled.span`
  margin-top: 3px;
  font-family: var(--font-serif);
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
`;

const Nav = styled.nav`
  display: flex;
  gap: 28px;
  align-items: center;

  @media (max-width: 720px) {
    gap: 14px;
  }
`;

const NavItem = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'disabled',
})`
  font-size: var(--font-size-sm);
  color: ${props => props.disabled ? 'var(--color-text-muted)' : props.active ? 'var(--color-accent)' : 'var(--color-silver)'};
  font-weight: ${props => props.disabled ? 'var(--font-weight-medium)' : props.active ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)'};
  text-decoration: none;
  transition: all var(--transition-speed) ease;
  position: relative;
  padding: 8px 0;
  border-bottom: 2px solid ${props => props.active ? 'var(--color-bronze)' : 'transparent'};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  background-color: transparent;
  
  &:hover {
    color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-primary)'};
    border-bottom-color: ${props => props.disabled ? 'transparent' : 'var(--color-border)'};
  }
  
  &:focus {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
  
  @media (max-width: 560px) {
    display: none;
  }
`;

const LogoutButton = styled.button`
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  color: var(--color-silver);
  padding: 8px 0;
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.default};
  
  &:hover {
    color: var(--color-primary);
    border-bottom-color: var(--color-border);
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 2px;
  }
`;

const Header = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  return (
    <HeaderContainer className={scrolled ? 'scrolled' : ''}>
      <NavContent>
        <Logo to="/" aria-label="Collector's Eye admin home">
          <LogoImage src={logoSrc} alt="" aria-hidden="true" />
          <LogoText>
            <LogoPrimary>Collector&apos;s Eye</LogoPrimary>
            <LogoSecondary>Admin Console</LogoSecondary>
          </LogoText>
        </Logo>
        <Nav>
          <NavItem
            to="/insights"
            active={location.pathname.includes('/insights') ? 1 : 0}
          >
            Insights
          </NavItem>
          <NavItem 
            to="/artists" 
            active={location.pathname.includes('/artists') ? 1 : 0}
          >
            Artist Management
          </NavItem>
          <NavItem
            to="/collectors"
            active={location.pathname.includes('/collectors') ? 1 : 0}
          >
            Collectors
          </NavItem>
          <NavItem 
            to="/support" 
            active={location.pathname.includes('/support') ? 1 : 0}
          >
            Support
          </NavItem>
          <NavItem 
            to="/reports" 
            active={location.pathname.includes('/reports') ? 1 : 0}
          >
            Reports
          </NavItem>
          <NavItem
            to="/disputes"
            active={location.pathname.includes('/disputes') ? 1 : 0}
          >
            Disputes
          </NavItem>
          <NavItem to="/shipping" active={location.pathname.includes('/shipping') ? 1 : 0}>
            Shipments
          </NavItem>
          <LogoutButton onClick={handleLogout}>
            Sign Out
          </LogoutButton>
        </Nav>
      </NavContent>
    </HeaderContainer>
  );
};

export default Header;
